import {
  FC,
  forwardRef,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  AuthContext,
  setBonusCode,
  useApi,
  uuid,
} from '@repo-level/core-front';

import { BonusI } from 'api/cms/bonuses';
import { Button } from 'components/buttons';
import { ModalDefaultProps } from 'components/Modal';
import { Spinner } from 'components/Spinner';
import { filterBonusGroups } from 'helpers/common';
import { CROSS_SALE_STAGS } from 'helpers/conf';
import { LocalStorage, LocalStorageKeys } from 'helpers/localStorage';
import { AddonsContext } from 'contexts/addons';
import { groupMemberBR } from 'helpers/user';
import { ModalTemplate } from 'components/ModalTemplate';
import { Logo } from 'components/Logo';
import { FormGroup } from 'components/form/groups/FormGroup';
import { Checkbox } from 'components/form/Checkbox';
import { ModalsContext, ModalType } from 'contexts/modal';
import { REASON_FOR_UPDATING_USER } from '@repo-level/core-front/contexts/auth/reducer';
import { ModalTemplateSettings } from 'components/ModalTemplate/types';
import { RegPayments } from '../components/RegPayments';
import { StagContext } from 'contexts/stag';

import { ScOffer, ScSkipBonusField, ScWelcome } from './styled';

import postregBg from 'assets/images/postreg/postregBg.png';
import postregCharacter from 'assets/images/postreg/postregCharacter.png';
import { ImageComponent } from 'components/Image';
import { useMedia } from 'modules/common/hooks/useMedia';

interface Benefit {
  title: string;
  description: string;
}

const localStorageSpecialStag = new LocalStorage<string, null>(
  'specialStag',
  null
);

export const Welcome = forwardRef<HTMLDivElement, ModalDefaultProps>(
  ({ onClose }, ref) => {
    const {
      t,
      i18n: { language },
    } = useTranslation('common');

    const { user, fetchUser, pendingUser } = useContext(AuthContext);
    const { customOffer, bonusGroups, pendingBonusGroups } = useContext(
      AddonsContext
    );
    const { handleModal } = useContext(ModalsContext);
    const { isMobileS } = useMedia();
    const { isStagNDB } = useContext(StagContext);
    const hasConfirmEmailShown = localStorage.getItem(
      LocalStorageKeys.ConfirmEmailNDB
    );

    const handleClose = useCallback(() => {
      onClose && onClose();

      if (isStagNDB && !hasConfirmEmailShown) {
        handleModal(ModalType.ConfirmEmailForNDBModal)();
      }
    }, [onClose, isStagNDB, hasConfirmEmailShown, handleModal]);

    const modalSettings: ModalTemplateSettings = useMemo(() => {
      return isMobileS
        ? {
            type: 'mobile',
            mode: {
              type: 'background-custom',
              src: postregBg,
              position: 'left top',
            },
          }
        : {
            type: 'desktop',
            size: 'large',
            mode: {
              type: 'background-custom',
              src: postregBg,
              position: 'right top',
            },
          };
    }, [isMobileS]);

    const [isWelcomeBonusEnabled, setWelcomeBonusEnabled] = useState(true);

    const isBonusRestricted = useMemo(() => groupMemberBR(user), [user]);

    const availableCrossSaleBanner = CROSS_SALE_STAGS.some(
      (stag) => stag === localStorageSpecialStag.get()
    );

    const [requestsSetBonusCode, { pending: pendingSetBonusCode }] = useApi(
      setBonusCode
    );

    const filteredBonusGroups = useMemo(
      () => filterBonusGroups(bonusGroups || [], user),
      [bonusGroups, user]
    );

    const firstBonus = useMemo(() => {
      if (filteredBonusGroups?.[0]?.bonuses == null) return null;

      return availableCrossSaleBanner
        ? {
            offer: '150% + 100 FS',
            code: '',
          }
        : (customOffer?.bonuses?.[0] as BonusI) ||
            filteredBonusGroups[0].bonuses[0];
    }, [availableCrossSaleBanner, filteredBonusGroups, customOffer]);

    const handleSubmitCode = useCallback(async () => {
      if (isWelcomeBonusEnabled) {
        const { error } = await requestsSetBonusCode({
          depositBonusCode: firstBonus?.code || '',
        }).request;

        if (error != null) {
          handleModal(ModalType.Deposit)();
          return;
        }

        fetchUser(REASON_FOR_UPDATING_USER.DEPOSIT);
      }

      handleModal(ModalType.Deposit)();
    }, [
      requestsSetBonusCode,
      fetchUser,
      isWelcomeBonusEnabled,
      handleModal,
      firstBonus?.code,
    ]);

    const skipBonusField = useMemo(
      () =>
        !isBonusRestricted && (
          <ScSkipBonusField>
            <FormGroup>
              <Checkbox
                key={uuid()}
                label={t('welcome.skipBonus')}
                field="notReceiveBonuses"
                initialValue={!isWelcomeBonusEnabled}
                onClick={() => setWelcomeBonusEnabled((s) => !s)}
              />
            </FormGroup>
          </ScSkipBonusField>
        ),
      [isBonusRestricted, isWelcomeBonusEnabled, t]
    );

    return (
      <>
        {pendingBonusGroups ? (
          <Spinner size={64} absoluteCenter />
        ) : (
          <ModalTemplate
            ref={ref}
            onClose={handleClose}
            settings={modalSettings}
          >
            <ScWelcome>
              <div className="content">
                <Logo height={28} />
                <header>
                  <p className="description">
                    {isBonusRestricted || !isWelcomeBonusEnabled
                      ? t('welcome.descriptionNoBonus')
                      : t('welcome.description')}
                  </p>
                  {pendingUser ? (
                    <Spinner size={64} />
                  ) : (
                    isWelcomeBonusEnabled &&
                    !isBonusRestricted && (
                      <OfferText offerText={firstBonus?.offer} />
                    )
                  )}
                </header>
                <div className={`action-btn ${language.toLowerCase()}`}>
                  <Button
                    green
                    xLarge
                    wide
                    pending={pendingSetBonusCode}
                    animated
                    onClick={handleSubmitCode}
                  >
                    {t('welcome.btn')}
                  </Button>
                </div>
                <ul className="benefits">
                  {Object.values(
                    t('welcome.benefits', {
                      returnObjects: true,
                    }) as Benefit[]
                  ).map(({ title, description }, index) => (
                    <li key={index}>
                      <span className="title">{title}</span>
                      <span className="description">{description}</span>
                    </li>
                  ))}
                </ul>
                {!isMobileS && (
                  <>
                    {skipBonusField}
                    <RegPayments />
                  </>
                )}
              </div>

              <ImageComponent
                width={320}
                height={360}
                src={postregCharacter}
                className="character"
                alt="character"
              />
            </ScWelcome>
            {isMobileS && (
              <>
                {skipBonusField}
                <RegPayments marginTop={32} />
              </>
            )}
          </ModalTemplate>
        )}
      </>
    );
  }
);

interface OfferTextProps {
  offerText?: string;
}

export const OfferText: FC<OfferTextProps> = ({ offerText }) => {
  const parsedOffer = offerText?.split('<br />');

  return parsedOffer ? (
    <ScOffer>
      <div className="offer">{parsedOffer[0]}</div>
      <div className="offer">{parsedOffer[1]}</div>
    </ScOffer>
  ) : null;
};
